// src/utils/supabaseClient.js
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase environment variables');
}

// Custom fetch function to intercept auth requests and redirect to backend
const customFetch = async (url, options = {}) => {
  const backendUrl = process.env.REACT_APP_SERVER_URL;
  
  // Convert URL to string for checking
  const urlString = url?.toString() || url;
  
  // Check if this is an auth token refresh request
  if (urlString && urlString.includes('/auth/v1/token') && urlString.includes('grant_type=refresh_token')) {
    try {
      // Redirect to backend API
      const backendAuthUrl = `${backendUrl}/auth/v1/token?grant_type=refresh_token`;
      
      // Prepare headers - preserve existing headers but ensure Content-Type
      const headers = new Headers(options.headers || {});
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }

      // Prepare backend request options
      const backendOptions = {
        method: options.method || 'POST',
        headers: headers,
        credentials: 'include', // Include credentials for CORS
      };

      // Preserve the original body if it exists (Supabase sends refresh_token in body)
      if (options.body) {
        backendOptions.body = options.body;
      }

      // Make request to backend instead
      const response = await fetch(backendAuthUrl, backendOptions);
      
      // If the backend request fails, try to fallback to original Supabase endpoint
      if (!response.ok) {
        console.warn('Backend token refresh failed, falling back to Supabase:', response.status, response.statusText);
        
        // Try to get error details for debugging
        try {
          const errorData = await response.clone().json();
          console.error('Token refresh error details:', errorData);
        } catch (e) {
          // If we can't parse the error, just log the status
          console.error('Token refresh failed with status:', response.status);
        }
        
        // Fallback: Let Supabase handle the refresh directly (bypass backend)
        // This allows the app to continue working even if backend refresh fails
        return fetch(url, options);
      }
      
      return response;
    } catch (error) {
      console.error('Error in token refresh request, falling back to Supabase:', error);
      // Fallback: Let Supabase handle the refresh directly
      return fetch(url, options);
    }
  }

  // For all other requests, use the default fetch
  return fetch(url, options);
};

const supabaseHandler = createClient(supabaseUrl, supabaseKey,{
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
}, {
  global: {
    fetch: customFetch,
  },
});

export default supabaseHandler;
